import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabaseClient'

export function useListingsByLocationFetcher({
  bounds = null,
  priceRange = [0, Infinity],
  limit = 120,
}) {
  const [listings, setListings] = useState([])
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState(null)
  // Track listings we've already seen by ID
  const seenListingIds = useRef(new Set())
  // Track the current bounds key to detect changes
  const currentBoundsKey = useRef('')

  // Extract individual bounds values from the Leaflet bounds object
  const south = bounds?.getSouth()
  const north = bounds?.getNorth()
  const west = bounds?.getWest()
  const east = bounds?.getEast()

  // Create a key for the current bounds to track changes
  const boundsKey = bounds ? `${south}-${north}-${west}-${east}` : ''

  const fetchListings = useCallback(
    async (pageNum) => {
      if (!bounds) return // Don't fetch if bounds aren't set yet

      setLoading(true)

      try {
        // Call the RPC function that uses PostGIS spatial filtering
        const { data, error } = await supabase
          .rpc('listings_in_view_by_location', {
            min_lat: south,
            min_long: west,
            max_lat: north,
            max_long: east,
          })
          .range(pageNum * limit, pageNum * limit + limit - 1)

        if (error) {
          setError(error.message)
          return
        }

        // Optionally filter by price client-side if needed
        const filteredData = data.filter((listing) => {
          const price = listing.price || 0
          return price >= priceRange[0] && price <= priceRange[1]
        })

        // Only add listings we haven't seen before
        const newListings = filteredData.filter(
          (listing) => !seenListingIds.current.has(listing.id)
        )

        // Update the set of seen listings
        newListings.forEach((listing) => seenListingIds.current.add(listing.id))

        // Append new listings to the existing ones
        setListings((prevListings) => [...prevListings, ...newListings])

        // Update hasMore based on if we received a full page of results
        setHasMore(data.length === limit)

        return newListings.length
      } catch (err) {
        console.error('Error fetching listings:', err)
        setError(err.message)
        return 0
      } finally {
        setLoading(false)
      }
    },
    [south, north, west, east, priceRange, limit, bounds]
  )

  // Load next page
  const loadNextPage = useCallback(() => {
    if (hasMore && !loading) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchListings(nextPage)
    }
  }, [hasMore, loading, page, fetchListings])

  // When bounds change (map moves), reset page but KEEP existing listings
  useEffect(() => {
    // Only process if we have bounds
    if (!bounds) return

    // Check if bounds have changed
    if (boundsKey !== currentBoundsKey.current) {
      // Store the new bounds key
      currentBoundsKey.current = boundsKey

      // Reset page counter for the new view
      setPage(0)

      // Fetch first page of new area (without resetting existing listings)
      fetchListings(0)
    }
  }, [boundsKey, fetchListings])

  return { listings, loading, error, hasMore, loadNextPage, page }
}
